import { useNavigate, useSearch } from "@tanstack/react-router";
import { type ReactNode, createContext, useContext } from "react";
import { z } from "zod";

import { usePreference } from "src/hooks/usePreference";

export const programGridZoomSearchSchema = z.coerce
  .number()
  .pipe(z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]))
  .optional()
  .catch(undefined);

export type ProgramGridZoomLevel = NonNullable<
  z.infer<typeof programGridZoomSearchSchema>
>;

export interface ProgramGridZoomContextValue {
  zoomLevel: ProgramGridZoomLevel;
  cellMinHeight: number;
  setZoomLevel: (level: ProgramGridZoomLevel) => void;
}

interface ProgramGridZoomProviderProps {
  children: ReactNode;
}

export const DEFAULT_ZOOM_LEVEL: ProgramGridZoomLevel = 4;

export const CELL_MIN_HEIGHT_BY_ZOOM_LEVEL: Record<
  ProgramGridZoomLevel,
  number
> = {
  1: 120,
  2: 160,
  3: 220,
  4: 280,
};

export const ProgramGridZoomContext =
  createContext<ProgramGridZoomContextValue | null>(null);

export function ProgramGridZoomProvider(props: ProgramGridZoomProviderProps) {
  const navigate = useNavigate();
  const searchZoom = useSearch({
    strict: false,
    select: (search) => {
      return search.zoom;
    },
  });
  const [savedZoomLevel, setSavedZoomLevel] = usePreference("programGridZoom");
  const zoomLevel = searchZoom ?? savedZoomLevel;

  function setZoomLevel(level: ProgramGridZoomLevel) {
    setSavedZoomLevel(level);
    navigate({
      to: ".",
      search: (prev) => ({ ...prev, zoom: level }),
      replace: true,
      resetScroll: false,
    });
  }

  const value: ProgramGridZoomContextValue = {
    zoomLevel,
    cellMinHeight: CELL_MIN_HEIGHT_BY_ZOOM_LEVEL[zoomLevel],
    setZoomLevel,
  };

  return (
    <ProgramGridZoomContext.Provider value={value}>
      {props.children}
    </ProgramGridZoomContext.Provider>
  );
}

export function useProgramGridZoom(): ProgramGridZoomContextValue {
  const context = useContext(ProgramGridZoomContext);

  if (!context) {
    throw new Error(
      "useProgramGridZoom must be used within ProgramGridZoomProvider.",
    );
  }

  return context;
}
